<template>
  <sidenav
    v-if="showSidenav"
    :class="[isRTL ? 'fixed-end' : 'fixed-start']"
  />
  <main class="main-content position-relative">
    <app-top-bar v-if="showSidenav" />
    <div class="page-view">
      <router-view />
    </div>
  </main>
</template>

<script>
import { mapState } from "vuex";
import Sidenav from "./examples/Sidenav";
import AppTopBar from "./examples/AppTopBar.vue";

export default {
  name: "App",
  components: {
    Sidenav,
    AppTopBar,
  },
  computed: {
    ...mapState(["isRTL", "showSidenav"]),
  },
  watch: {
    showSidenav: {
      immediate: true,
      handler(visible) {
        this.syncAppShellClass(visible);
      },
    },
  },
  beforeMount() {
    this.$store.state.isTransparent = "bg-white";
  },
  methods: {
    syncAppShellClass(visible) {
      const el = document.getElementById("app");
      if (!el) return;
      el.classList.toggle("g-sidenav-show", !!visible);
    },
  },
};
</script>

<style>
/* ═══════════════════════════════════════════════
   Sakai-inspired Design System for TestOps Hub
   ═══════════════════════════════════════════════ */

:root {
  --surface-ground: #f4f6f9;
  --surface-card: #ffffff;
  --surface-border: #e9ecef;
  --text-primary: #344767;
  --text-secondary: #67748e;
  --primary: #344767;
  --primary-light: rgba(52, 71, 103, 0.08);
  --primary-hover: rgba(52, 71, 103, 0.14);
  --radius-card: 12px;
  --shadow-card: 0 1px 4px rgba(0,0,0,0.07), 0 2px 10px rgba(0,0,0,0.05);
  --app-topbar-height: 4rem;
}

/* ── Page background ── */
html,
body,
#app {
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

body {
  background-color: var(--surface-ground) !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif !important;
  color: var(--text-primary) !important;
}

.main-content {
  background-color: var(--surface-ground) !important;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.app-topbar {
  flex-shrink: 0;
}

.page-view {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

/* ── Sidebar: full-height flat design ── */
.sidenav {
  z-index: 1030 !important;
  background: var(--surface-card) !important;
  border-right: 1px solid var(--surface-border) !important;
  box-shadow: none !important;
  margin: 0 !important;
  border-radius: 0 !important;
  top: 0 !important;
}

@media (min-width: 1200px) {
  .sidenav.fixed-start ~ .main-content {
    margin-left: 15.625rem !important;
  }
}

/* ── Sidebar header ── */
.sidenav-header {
  border-bottom: 1px solid var(--surface-border);
}

/* ── Sidebar nav items ── */
.navbar-vertical .navbar-nav > .nav-item .nav-link {
  border-radius: 0.5rem !important;
  margin: 1px 0.75rem !important;
  padding: 0.6rem 0.85rem !important;
  transition: background 0.15s ease, color 0.15s ease;
  width: calc(100% - 1.5rem) !important;
}

.navbar-vertical .navbar-nav > .nav-item .nav-link:hover:not(.active) {
  background: rgba(52, 71, 103, 0.05) !important;
  box-shadow: none !important;
}

/* ── Active nav item: Sakai left-accent style ── */
.navbar-vertical .navbar-nav > .nav-item .nav-link.active {
  background: var(--primary-light) !important;
  color: var(--primary) !important;
  box-shadow: none !important;
  border-left: 3px solid var(--primary) !important;
  border-radius: 0 0.5rem 0.5rem 0 !important;
  padding-left: calc(0.85rem - 3px) !important;
}

.navbar-vertical .navbar-nav > .nav-item .nav-link.active .sidenav-icon i,
.navbar-vertical .navbar-nav > .nav-item .nav-link.active .nav-link-text {
  color: var(--primary) !important;
  opacity: 1 !important;
}

/* ── Sidebar icon ── */
.sidenav-icon {
  width: 1.8rem;
  min-width: 1.8rem;
  height: 1.8rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  flex-shrink: 0;
}

/* ── Nav link text ── */
.navbar-vertical .nav-link-text {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.navbar-vertical .navbar-nav > .nav-item .nav-link.active .sidenav-icon i {
  color: var(--primary) !important;
}

/* ── Section header label ── */
.sidenav-section-label {
  color: #adb5bd !important;
  letter-spacing: 0.08em;
  font-size: 0.65rem !important;
  padding-left: 1.5rem !important;
  margin-top: 0.25rem !important;
}

/* ── Cards: Sakai style ── */
.card {
  border-radius: var(--radius-card) !important;
  box-shadow: var(--shadow-card) !important;
  border: 1px solid var(--surface-border) !important;
  background: var(--surface-card) !important;
}

.card-header {
  background: var(--surface-card) !important;
  border-bottom: 1px solid var(--surface-border) !important;
  border-radius: var(--radius-card) var(--radius-card) 0 0 !important;
  padding: 1rem 1.25rem 0.75rem !important;
}

.card-body {
  padding: 1rem 1.25rem !important;
}

/* ── Table styles ── */
.table th {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary) !important;
  border-bottom: 1px solid var(--surface-border) !important;
  padding: 0.6rem 0.75rem !important;
}

.table td {
  font-size: 0.8rem;
  color: var(--text-primary);
  border-bottom: 1px solid #f1f3f5 !important;
  padding: 0.65rem 0.75rem !important;
  vertical-align: middle;
}

.table tbody tr:last-child td {
  border-bottom: none !important;
}

.table tbody tr:hover td {
  background: #f8f9fa;
}

/* ── Stat card icon shape ── */
.icon.icon-shape {
  border-radius: 10px !important;
  width: 2.8rem !important;
  height: 2.8rem !important;
}

/* ── Buttons ── */
.btn {
  border-radius: 8px !important;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
}

/* ── Page container padding ── */
.container-fluid {
  padding-top: 1.5rem !important;
  padding-left: 1.75rem !important;
  padding-right: 1.75rem !important;
  padding-bottom: 1.5rem !important;
}

/* ── Prevent Bootstrap row negative margin overflow ── */
.row {
  --bs-gutter-x: 1rem;
}

/* ── Headings ── */
h4, h5, h6 {
  color: var(--text-primary);
  font-weight: 700;
}

/* ── Horizontal rule ── */
.horizontal.dark {
  background-image: linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.1), rgba(0,0,0,0)) !important;
}

/* ── Tab styles ── */
.nav-tabs {
  border-bottom: 1px solid var(--surface-border) !important;
}

.nav-tabs .nav-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;
  color: var(--text-secondary) !important;
}

.nav-tabs .nav-link.active {
  color: var(--primary) !important;
  border-bottom: 2px solid var(--primary) !important;
  background: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

/* ── Badge / Pill styles ── */
.badge {
  border-radius: 6px !important;
  font-size: 0.7rem !important;
  font-weight: 600 !important;
  padding: 0.3em 0.65em !important;
}

/* ── Scrollbar ── */
::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #dee2e6;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #ced4da;
}

/* ── Shared checkbox style ── */
.bo-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  cursor: pointer;
  user-select: none;
}

.bo-checkbox-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.bo-checkbox-box {
  width: 18px;
  height: 18px;
  border-radius: 0.7rem;
  border: 2px solid #2c2f36;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.bo-checkbox-box::after {
  content: "";
  width: 5px;
  height: 9px;
  border-right: 2px solid transparent;
  border-bottom: 2px solid transparent;
  transform: rotate(45deg) translate(-1px, -1px);
}

.bo-checkbox-input:checked + .bo-checkbox-box {
  background: #191970;
  border-color: #191970;
}

.bo-checkbox-input:checked + .bo-checkbox-box::after {
  border-right-color: #fff;
  border-bottom-color: #fff;
}

.bo-checkbox-input:focus-visible + .bo-checkbox-box {
  box-shadow: 0 0 0 4px rgba(105, 65, 198, 0.2);
}

.bo-checkbox-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: #2c2f36;
  line-height: 1.1;
}

.bo-checkbox--sm {
  gap: 0.45rem;
}

.bo-checkbox--sm .bo-checkbox-box {
  width: 18px;
  height: 18px;
  border-width: 2px;
  border-radius: 0.32rem;
}

.bo-checkbox--sm .bo-checkbox-box::after {
  width: 5px;
  height: 9px;
  border-right-width: 2px;
  border-bottom-width: 2px;
}

.bo-checkbox--sm .bo-checkbox-label {
  font-size: 0.8rem;
}

/* Shared assignee pill style */
.bo-assignee-pill {
  display: inline-flex !important;
  align-items: center !important;
  background: #e8f4fd !important;
  color: #0c7a9a !important;
  border: 1px solid #bee5eb !important;
  border-radius: 999px !important;
  font-size: 0.7rem !important;
  font-weight: 500 !important;
  line-height: 1.1 !important;
  padding: 0.12rem 0.5rem !important;
}
</style>
